import { createContext, useContext } from "react";
import type { Project, Scan, ScanGraphNode, ScanListItem, SubscriptionInfo } from "./api";

export type ScanGraph = {
  scan_id: string;
  pipeline_name: string;
  pipeline_version: string;
  nodes: ScanGraphNode[];
  edges: Array<{ from: string; to: string; kind: string }>;
};

export type AppValue = {
  projects: Project[];
  activeProject: Project | null;
  setActiveProjectId: (id: string) => void;
  activeScan: Scan | null;
  /** Live pipeline graph for the *viewed* scan (defaults to the active scan)
   * — fetched alongside the Scan poll so ScanBar can compute true per-stage
   * progress. Must track whatever scan is on screen, else getScanStages can't
   * match it (graph.scan_id ≠ shown scan) and silently falls back to a flat
   * "all bars = overall %" approximation. */
  activeScanGraph: ScanGraph | null;
  /** Pause bookkeeping for the active-time scan timer (see useScanElapsed).
   * `pausedMsByScan` is the time each scan has spent in finished pauses;
   * `activePause` is the currently-open pause. Client-side only — both reset on
   * reload, since the backend exposes no paused-duration field. */
  pausedMsByScan: Record<string, number>;
  activePause: { scanId: string; since: number } | null;
  viewedScan: Scan | null;
  viewedScanId: string | null;
  setViewedScanId: (id: string | null) => void;
  projectScans: Record<string, ScanListItem[] | undefined>;
  loadProjectScans: (projectId: string) => Promise<void>;
  subscription: SubscriptionInfo | null;
  /** True when the backend returned 412/FAILED_PRECONDITION on /billing/subscription
   * — user has no active plan yet (post-registration state). */
  noPlan: boolean;
  loading: boolean;
  initialized: boolean;
  error: string | null;
  reload: () => Promise<void>;
  /** Refetch only /billing/subscription. Used by the post-checkout poll, which
   * waits for an asynchronous provider webhook and must not refetch projects
   * and scans on every tick. */
  refreshSubscription: () => Promise<void>;
  createProject: (input: {
    name: string;
    description?: string;
    jurisdiction_code?: string;
  }) => Promise<Project>;
  deleteProject: (id: string) => Promise<void>;
  /** Change the report jurisdiction of an existing project via PATCH and merge
   * the refreshed project (with its resolved jurisdiction_code/name) into
   * state. Callers pass a code only for a real change to an enabled
   * jurisdiction — see jurisdictionPatchCode. */
  updateProjectJurisdiction: (projectId: string, code: string) => Promise<Project>;
  /** Trust our own observation that every target of this project is verified,
   * overriding the project's (asynchronously updated) counters until the
   * backend reports the same. See AppStateProvider.locallyVerified. */
  markProjectVerified: (projectId: string) => void;
  /** Pause/resume/cancel a scan. Defaults to the active scan; pass a scan id
   * to target the scan the user is actually viewing (ScanStrip does this). */
  pauseScan: (scanId?: string) => Promise<void>;
  resumeScan: (scanId?: string) => Promise<void>;
  cancelScan: (scanId?: string) => Promise<void>;
  startScan: (domainId: string) => Promise<Scan | null>;
  startScanForProject: (projectId: string) => Promise<Scan | null>;
  scanIntroToken: number;
};

export const AppContext = createContext<AppValue | null>(null);

export function useAppState(): AppValue {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useAppState must be used within AppStateProvider");
  return ctx;
}
